import React, { useEffect, useState, useMemo } from "react";
import { AgGridReact } from "ag-grid-react";
import {
  ColDef,
  ICellRendererParams,
  themeQuartz,
  CellStyle,
} from "ag-grid-community";
import "ag-grid-community/styles/ag-grid.css";
import "ag-grid-community/styles/ag-theme-alpine.css";
import { Settings, Plus, Edit2, Trash2, ArrowUp } from "lucide-react";
import { Modal, message, Switch, Tooltip } from "antd";
import { useNavigate } from "react-router-dom";
import {
  useRoundNotificationStore,
  EscalationRule,
} from "../../../store/RoundNotificationStore";
import { PageLoader } from "../../../components/loader/Loader";
import EscalationRuleForm from "./EscalationRuleForm";

const gridTheme = themeQuartz.withParams({
  backgroundColor: "#fff",
  foregroundColor: "#2a2a2a",
  headerTextColor: "#2a2a2a",
  headerBackgroundColor: "#E9F5FF",
  oddRowBackgroundColor: "rgb(0, 0, 0, 0.03)",
  headerColumnResizeHandleColor: "rgb(126, 46, 132)",
  fontSize: "0.80rem",
  headerFontWeight: "bold",
});

const EscalationRuleList: React.FC = () => {
  const navigate = useNavigate();

  // Use Zustand store with selectors
  const escalationRules = useRoundNotificationStore(
    (state) => state.escalationRules,
  );
  const defaults = useRoundNotificationStore((state) => state.defaults);
  const rulesLoading = useRoundNotificationStore((state) => state.rulesLoading);
  const fetchEscalationRules = useRoundNotificationStore(
    (state) => state.fetchEscalationRules,
  );
  const fetchDefaults = useRoundNotificationStore(
    (state) => state.fetchDefaults,
  );
  const updateEscalationRule = useRoundNotificationStore(
    (state) => state.updateEscalationRule,
  );
  const deleteEscalationRuleAction = useRoundNotificationStore(
    (state) => state.deleteEscalationRule,
  );
  const [deleteModalVisible, setDeleteModalVisible] = useState(false);
  const [ruleToDelete, setRuleToDelete] = useState<string | null>(null);

  // Modal State
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [editingRuleId, setEditingRuleId] = useState<string | undefined>(
    undefined,
  );

  useEffect(() => {
    fetchEscalationRules();
    fetchDefaults();
  }, [fetchEscalationRules, fetchDefaults]);

  const handleDelete = async () => {
    if (ruleToDelete) {
      const success = await deleteEscalationRuleAction(ruleToDelete);
      if (success) {
        message.success("Rule deleted successfully");
        setDeleteModalVisible(false);
        setRuleToDelete(null);
      } else {
        message.error("Failed to delete rule");
      }
    }
  };

  const handleToggleActive = async (rule: EscalationRule) => {
    const updated = await updateEscalationRule(rule._id, {
      isActive: !rule.isActive,
    });
    if (updated) {
      message.success(`Rule ${updated.isActive ? "activated" : "deactivated"}`);
    } else {
      message.error("Failed to update rule");
    }
  };

  const handleOpenCreateModal = () => {
    setEditingRuleId(undefined);
    setIsModalVisible(true);
  };

  const handleOpenEditModal = (id: string) => {
    setEditingRuleId(id);
    setIsModalVisible(true);
  };

  const handleModalClose = () => {
    setIsModalVisible(false);
    setEditingRuleId(undefined);
  };

  const handleFormSuccess = () => {
    handleModalClose();
    fetchEscalationRules();
  };

  // Cell Renderers
  const TriggerEventRenderer = (params: ICellRendererParams) => {
    const event = params.value;
    const labels: Record<string, { label: string; color: string }> = {
      critical_issue: {
        label: "Critical Issue",
        color: "bg-red-100 text-red-800",
      },
      missed_round: {
        label: "Missed Round",
        color: "bg-orange-100 text-orange-800",
      },
      sla_breach: {
        label: "SLA Breach",
        color: "bg-yellow-100 text-yellow-800",
      },
      repeat_noncompliance: {
        label: "Repeat Issue",
        color: "bg-pink-100 text-pink-800",
      },
    };
    const cfg = labels[event] || {
      label: event,
      color: "bg-gray-100 text-gray-800",
    };
    return (
      <span
        className={`px-2 py-1 rounded-full text-xs font-semibold ${cfg.color}`}
      >
        {cfg.label}
      </span>
    );
  };

  const RoundTypeRenderer = (params: ICellRendererParams) => {
    const type = params.value;
    const colors: Record<string, string> = {
      Safety: "bg-blue-100 text-blue-800",
      Facility: "bg-green-100 text-green-800",
      Fire: "bg-red-100 text-red-800",
      "Infection Control": "bg-purple-100 text-purple-800",
      All: "bg-gray-100 text-gray-800",
    };
    return (
      <span
        className={`px-2 py-1 rounded text-xs font-medium ${colors[type] || "bg-gray-100"}`}
      >
        {type}
      </span>
    );
  };

  const EscalationLevelsRenderer = (params: ICellRendererParams) => {
    const levels = params.value || [];
    return (
      <div className="flex items-center gap-1">
        {levels
          .slice(0, 3)
          .map(
            (
              level: { level: number; roleName: string; timeMinutes: number },
              idx: number,
            ) => (
              <Tooltip
                key={idx}
                title={`L${level.level}: ${level.roleName} @ ${level.timeMinutes}min`}
              >
                <span className="flex items-center gap-0.5 px-1.5 py-0.5 bg-purple-50 text-purple-700 rounded text-xs">
                  <ArrowUp size={10} />L{level.level}
                </span>
              </Tooltip>
            ),
          )}
        {levels.length > 3 && (
          <span className="text-xs text-gray-500">+{levels.length - 3}</span>
        )}
      </div>
    );
  };

  const ActiveRenderer = (params: ICellRendererParams) => {
    const isActive = params.value;
    const rule = params.data;
    return (
      <Switch
        checked={isActive}
        onChange={() => handleToggleActive(rule)}
        size="small"
        className={isActive ? "bg-green-500" : "bg-gray-300"}
      />
    );
  };

  const ActionsRenderer = (params: ICellRendererParams) => {
    const rule = params.data;
    return (
      <div className="flex items-center gap-2">
        <Tooltip title="Edit">
          <button
            onClick={() => handleOpenEditModal(rule._id)}
            className="p-1.5 hover:bg-gray-100 rounded-full text-green-600 transition-colors"
          >
            <Edit2 size={16} />
          </button>
        </Tooltip>
        <Tooltip title="Delete">
          <button
            onClick={() => {
              setRuleToDelete(rule._id);
              setDeleteModalVisible(true);
            }}
            className="p-1.5 hover:bg-gray-100 rounded-full text-red-600 transition-colors"
          >
            <Trash2 size={16} />
          </button>
        </Tooltip>
      </div>
    );
  };

  const columnDefs = useMemo<ColDef[]>(
    () => [
      {
        field: "name",
        headerName: "Rule Name",
        flex: 1,
        minWidth: 180,
        cellStyle: { padding: "10px", fontWeight: "500" } as CellStyle,
      },
      {
        field: "triggerEvent",
        headerName: "Trigger Event",
        width: 150,
        cellRenderer: TriggerEventRenderer,
        cellStyle: { padding: "10px" } as CellStyle,
      },
      {
        field: "roundType",
        headerName: "Round Type",
        width: 140,
        cellRenderer: RoundTypeRenderer,
        cellStyle: { padding: "10px" } as CellStyle,
      },
      {
        field: "escalationLevels",
        headerName: "Escalation Levels",
        width: 180,
        cellRenderer: EscalationLevelsRenderer,
        sortable: false,
        cellStyle: { padding: "10px" } as CellStyle,
      },
      {
        field: "autoEscalationEnabled",
        headerName: "Auto",
        width: 80,
        valueFormatter: (params) => (params.value ? "Yes" : "No"),
        cellStyle: { padding: "10px", textAlign: "center" } as CellStyle,
      },
      {
        field: "isActive",
        headerName: "Active",
        width: 90,
        cellRenderer: ActiveRenderer,
        cellStyle: {
          padding: "10px",
          display: "flex",
          alignItems: "center",
        } as CellStyle,
      },
      {
        field: "createdAt",
        headerName: "Created",
        width: 120,
        valueFormatter: (params) =>
          params.value ? new Date(params.value).toLocaleDateString() : "-",
        cellStyle: { padding: "10px" } as CellStyle,
      },
      {
        field: "actions",
        headerName: "Actions",
        width: 100,
        pinned: "right",
        cellRenderer: ActionsRenderer,
        sortable: false,
        filter: false,
        cellStyle: { padding: "10px" } as CellStyle,
      },
    ],
    [],
  );

  const defaultColDef = useMemo(
    () => ({
      sortable: true,
      resizable: true,
    }),
    [],
  );

  if (rulesLoading && !escalationRules.length) {
    return (
      <div className="flex justify-center items-center h-screen">
        <PageLoader />
      </div>
    );
  }

  return (
    <div className="px-2 py-4 pt-16 mt-14 max-w-[1600px] mx-auto space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div className="flex items-center gap-3">
          <div>
            <h1 className="text-2xl text-gray-900">Escalation Rules</h1>
          </div>
        </div>
        <div className="flex items-center gap-3">
          <button
            onClick={() => navigate("/safety-facility-rounds/notifications")}
            className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors text-sm font-medium"
          >
            View Notifications
          </button>
          <button
            onClick={handleOpenCreateModal}
            className="flex items-center gap-2 px-4 py-2 bg-[#0fa9a6] text-white rounded-lg hover:bg-[#197f7d] transition-colors text-sm font-medium"
          >
            <Plus size={18} />
            Create Rule
          </button>
        </div>
      </div>

      {/* Info Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <div className="bg-gradient-to-r from-purple-500 to-indigo-600 p-6 rounded-xl text-[#fff]">
          <p className="text-sm text-[#fff] opacity-90 font-semibold">
            Total Rules
          </p>
          <p className="text-3xl text-[#fff] font-bold mt-1">
            {escalationRules.length}
          </p>
        </div>
        <div className="bg-gradient-to-r from-green-500 to-teal-600 p-6 rounded-xl text-[#fff]">
          <p className="text-sm text-[#fff] opacity-90 font-semibold">
            Active Rules
          </p>
          <p className="text-3xl text-[#fff] font-bold mt-1">
            {escalationRules.filter((r) => r.isActive).length}
          </p>
        </div>
        <div className="bg-gradient-to-r from-orange-500 to-red-600 p-6 rounded-xl text-[#fff]">
          <p className="text-sm text-[#fff] opacity-90 font-semibold">
            Auto-Escalation Enabled
          </p>
          <p className="text-3xl text-[#fff] font-bold mt-1">
            {escalationRules.filter((r) => r.autoEscalationEnabled).length}
          </p>
        </div>
      </div>

      {/* Default Levels Info */}
      {defaults?.defaultLevels && (
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
          <h3 className="text-sm font-semibold text-blue-800 mb-2">
            Default Escalation Chain
          </h3>
          <div className="flex items-center gap-4 flex-wrap">
            {defaults.defaultLevels.map((level, idx) => (
              <div key={idx} className="flex items-center gap-2">
                <span className="px-2 py-1 bg-blue-100 text-blue-800 rounded text-sm font-medium">
                  L{level.level}: {level.roleName}
                </span>
                <span className="text-xs text-blue-600">
                  @ {level.timeMinutes}min
                </span>
                {idx < defaults.defaultLevels.length - 1 && (
                  <ArrowUp className="rotate-90 text-blue-400" size={16} />
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Grid */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
        <div className="ag-theme-alpine w-full" style={{ height: "450px" }}>
          <AgGridReact
            rowData={escalationRules}
            columnDefs={columnDefs}
            theme={gridTheme}
            defaultColDef={defaultColDef}
            pagination={true}
            paginationPageSize={10}
            animateRows={true}
            rowHeight={55}
            headerHeight={55}
            suppressCellFocus={true}
            enableCellTextSelection={true}
          />
        </div>
      </div>

      {/* Form Modal */}
      <Modal
        title={
          editingRuleId ? "Edit Escalation Rule" : "Create Escalation Rule"
        }
        open={isModalVisible}
        onCancel={handleModalClose}
        footer={null}
        width={800}
        centered
        destroyOnClose
        zIndex={1050}
      >
        <EscalationRuleForm
          ruleId={editingRuleId}
          onSuccess={handleFormSuccess}
          onCancel={handleModalClose}
        />
      </Modal>

      {/* Delete Modal */}
      <Modal
        title="Delete Escalation Rule"
        open={deleteModalVisible}
        onOk={handleDelete}
        onCancel={() => {
          setDeleteModalVisible(false);
          setRuleToDelete(null);
        }}
        okText="Delete"
        cancelText="Cancel"
        okButtonProps={{ danger: true }}
        zIndex={1050}
      >
        <p>
          Are you sure you want to delete this escalation rule? This action
          cannot be undone.
        </p>
      </Modal>
    </div>
  );
};

export default EscalationRuleList;
